import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { approvalApi, teamApi } from '../lib/api';
import { PaperAirplaneIcon, XMarkIcon, DocumentTextIcon, UserCircleIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';

interface BulkSubmitModalProps {
  reportIds: string[];
  reports: any[];
  teamId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function BulkSubmitModal({
  reportIds,
  reports,
  teamId: initialTeamId,
  onClose,
  onSuccess,
}: BulkSubmitModalProps) {
  const queryClient = useQueryClient();
  const [selectedReviewer, setSelectedReviewer] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch user's teams to determine if we need a team selector
  const { data: teamsData } = useQuery({
    queryKey: ['teams', 'list-unwrapped'],
    queryFn: async () => {
      const response = await teamApi.getTeams();
      return response.data;
    },
  });

  const userTeams = teamsData?.teams || [];
  const needsTeamSelection = !initialTeamId && userTeams.length > 1;

  // If the user has exactly one team and no teamId was passed from reports, default to it
  const autoTeamId = !initialTeamId && userTeams.length === 1
    ? userTeams[0].id
    : initialTeamId;

  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  // The effective teamId: explicit from reports > user-selected > auto-resolved single team
  const effectiveTeamId = initialTeamId || selectedTeamId || autoTeamId;

  // Fetch team members to populate reviewer dropdown
  const { data: teamData } = useQuery({
    queryKey: ['team', effectiveTeamId],
    queryFn: async () => {
      if (!effectiveTeamId) return null;
      const response = await teamApi.getTeam(effectiveTeamId);
      return response.data;
    },
    enabled: !!effectiveTeamId,
  });

  // Filter to compliance officers and owners who can review
  const reviewers = teamData?.members?.filter(
    (m: any) => m.teamRole === 'COMPLIANCE_OFFICER' || m.teamRole === 'OWNER'
  ) || [];

  const handleSubmit = async () => {
    if (needsTeamSelection && !selectedTeamId) {
      toast.error('Please select a team');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await approvalApi.bulkSubmitForReview(
        reportIds,
        selectedReviewer || undefined,
        effectiveTeamId || undefined
      );

      const { submitted, failed } = response.data;

      // Invalidate queries to refresh the reports list
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reviews'] });

      if (failed === 0) {
        toast.success(`${submitted} report${submitted > 1 ? 's' : ''} submitted for review`);
      } else {
        toast.success(`${submitted} submitted, ${failed} failed`);
      }

      onSuccess();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Unable to submit reports for review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Submit {reportIds.length} Report{reportIds.length > 1 ? 's' : ''} for Review
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Report list */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reports to submit
            </label>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
              {reports.map((report: any) => (
                <div key={report.id} className="flex items-center px-3 py-2">
                  <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-sm text-gray-700 truncate">
                    {report.upload?.fileName || report.id}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Team selection — only shown when user has multiple teams and no team from reports */}
          {needsTeamSelection && (
            <div>
              <label
                htmlFor="team-select"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Select team
              </label>
              <div className="relative">
                <BuildingOfficeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  id="team-select"
                  value={selectedTeamId}
                  onChange={(e) => {
                    setSelectedTeamId(e.target.value);
                    setSelectedReviewer(''); // Reset reviewer when team changes
                  }}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Choose a team...</option>
                  {userTeams.map((team: any) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Reviewer selection */}
          {reviewers.length > 0 && (
            <div>
              <label
                htmlFor="reviewer-select"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Assign to reviewer (optional)
              </label>
              <div className="relative">
                <UserCircleIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  id="reviewer-select"
                  value={selectedReviewer}
                  onChange={(e) => setSelectedReviewer(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">All compliance officers</option>
                  {reviewers.map((member: any) => (
                    <option key={member.userId} value={member.userId}>
                      {member.user.firstName && member.user.lastName
                        ? `${member.user.firstName} ${member.user.lastName}`
                        : member.user.email}
                      {' '}({member.teamRole === 'OWNER' ? 'Owner' : 'Compliance Officer'})
                    </option>
                  ))}
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Leave blank to notify all compliance officers on the team
              </p>
            </div>
          )}

          {!effectiveTeamId && !needsTeamSelection && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                These reports are not assigned to a team. Assign them to a team first to enable reviewer selection and email notifications.
              </p>
            </div>
          )}

          <p className="text-sm text-gray-500">
            Reviewers will be notified by email and in-app notification.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || (needsTeamSelection && !selectedTeamId)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <PaperAirplaneIcon className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Submitting...' : 'Submit for Review'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
